import { BarChart3 } from 'lucide-react';
import { StatCard, Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';

interface HeadcountReport {
  total_active: number;
  by_department: Record<string, number>;
  by_type: Record<string, number>;
  remote_count: number;
  onsite_count: number;
  turnover: { terminated: number; rate_percent: number; year: number };
}

export function ReportsPage() {
  const { data, loading } = useApi<HeadcountReport>('reports/headcount');

  if (loading) return <div className="text-muted-foreground">Loading...</div>;
  if (!data) return <div className="text-muted-foreground">No data</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-amber-500" /> Reports & Analytics
      </h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Active Employees" value={data.total_active} accent />
        <StatCard label="Remote" value={data.remote_count} />
        <StatCard label="Onsite" value={data.onsite_count} />
        <StatCard label="Turnover Rate" value={`${data.turnover.rate_percent}%`} trend={`${data.turnover.terminated} departed in ${data.turnover.year}`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">By Department</h3>
          <div className="space-y-2">
            {Object.entries(data.by_department).sort((a, b) => b[1] - a[1]).map(([dept, count]) => (
              <div key={dept} className="flex items-center justify-between">
                <span className="text-sm text-foreground">{dept}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-surface-3 rounded-full h-2">
                    <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${(count / data.total_active) * 100}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">By Employment Type</h3>
          <div className="space-y-2">
            {Object.entries(data.by_type).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-sm text-foreground capitalize">{type.replace('_', ' ')}</span>
                <span className="text-sm text-amber-500 font-medium">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
